import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Eye, FileDown, Loader2, Mail, Plus, Printer, RefreshCw, Search } from "lucide-react";
import { getSickNote, listSickNotes, getMyAccess } from "@/lib/sick-notes.functions";
import { useSickNoteActions } from "@/components/admin/useSickNoteActions";
import { formatInvoiceDate as d } from "@/lib/invoice-details";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { SickNote } from "@/lib/sick-notes";

export const Route = createFileRoute("/_authenticated/admin/sick-notes/")({
  head: () => ({ meta: [{ title: "Sick Note History · Admin" }, { name: "robots", content: "noindex" }] }),
  component: HistoryPage,
});

function HistoryPage() {
  const [search, setSearch] = useState("");
  const listFn = useServerFn(listSickNotes);
  const getFn = useServerFn(getSickNote);
  const accessFn = useServerFn(getMyAccess);
  const actions = useSickNoteActions();
  const { data: access } = useQuery({ queryKey: ["sickNoteAccess"], queryFn: () => accessFn() });
  const { data: rows = [], isPending, error } = useQuery({
    queryKey: ["sickNotes", search],
    queryFn: () => listFn({ data: { search } }),
  });
  const can = (p: string) => access?.perms.includes(p as never);
  const withNote = async (id: string, f: (n: SickNote) => Promise<unknown> | void) => {
    const { note } = await getFn({ data: { id } });
    await f(note as SickNote);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search certificate no, patient, practitioner" />
        </div>
        {can("sick_notes.create") && (
          <Button asChild>
            <Link to="/admin/sick-notes/new"><Plus className="mr-2 h-4 w-4" />Create Sick Note</Link>
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          {isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : error ? (
            <p className="p-6 text-sm text-destructive">{(error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No sick notes issued yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    {["Certificate no", "Patient", "Consultation", "Leave start", "Leave end", "Practitioner", "Issued", "Status", "Actions"].map((h) => (
                      <th key={h} className="whitespace-nowrap px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: any) => (
                    <tr key={r.id} className="border-t">
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{r.certificate_number}{r.version > 1 && <span className="ml-1 text-muted-foreground">v{r.version}</span>}</td>
                      <td className="px-3 py-2">{r.patient_name}</td>
                      <td className="whitespace-nowrap px-3 py-2">{d(r.consultation_date)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{d(r.leave_start)}</td>
                      <td className="whitespace-nowrap px-3 py-2">{d(r.leave_end)}</td>
                      <td className="px-3 py-2">{r.practitioner_name}</td>
                      <td className="whitespace-nowrap px-3 py-2">{d(r.issued_at)}</td>
                      <td className="px-3 py-2">
                        <Badge variant={r.status === "issued" ? "default" : "secondary"}>{r.status === "issued" ? "Issued" : "Superseded"}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <Button asChild size="icon" variant="ghost" title="View"><Link to="/admin/sick-notes/$id" params={{ id: r.id }}><Eye className="h-4 w-4" /></Link></Button>
                          {can("sick_notes.pdf") && (
                            <>
                              <Button size="icon" variant="ghost" title="Download PDF" onClick={() => withNote(r.id, actions.download)}><FileDown className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" title="Print" onClick={() => withNote(r.id, actions.print)}><Printer className="h-4 w-4" /></Button>
                              {access?.admin && (
                                <Button size="icon" variant="ghost" title="Email patient" onClick={() => withNote(r.id, actions.email)}><Mail className="h-4 w-4" /></Button>
                              )}
                            </>
                          )}
                          {can("sick_notes.reissue") && r.status === "issued" && (
                            <Button asChild size="icon" variant="ghost" title="Reissue (corrected copy)">
                              <Link to="/admin/sick-notes/new" search={{ reissue: r.id }}><RefreshCw className="h-4 w-4" /></Link>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">Issued certificates can't be edited. To correct one, reissue it — a new certificate number is created and the original is kept as superseded.</p>
    </div>
  );
}
